import { Inject, Injectable, Optional } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { CORE_LAYOUT_MANAGE_IDB, ICoreLayoutManageIdbService } from 'core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { CHATBOT_ROLE_LOOKUP, ChatbotRoleLookup } from '../tokens/chatbot-api.tokens';

export interface ChatbotUserContext {
  readonly fullName: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly role: string | null;
}

interface AccountInfoRecord {
  readonly Name?: string;
  readonly LastName?: string;
  readonly UserName?: string;
}

interface UserRoleRecord {
  readonly Description?: string;
  readonly RoleCode?: string;
}

const IDB_ACCOUNT_INFO = 'accountInfo';
const IDB_USER_ROLES = 'userRoles';

/**
 * Resolves the identity fields required by the chat contract (username, email).
 * Preferred source is the MSAL active account (id token claims), per user-context findings.
 * Session storage values are used only as fallback when MSAL is unavailable.
 */
@Injectable()
export class ChatbotUserContextAdapter {
  constructor(
    @Optional() private readonly msalService: MsalService,
    @Optional() @Inject(CORE_LAYOUT_MANAGE_IDB) private readonly idbService: ICoreLayoutManageIdbService,
    @Optional() @Inject(CHATBOT_ROLE_LOOKUP) private readonly roleLookup: ChatbotRoleLookup,
  ) {}

  getUserContext(): ChatbotUserContext {
    const account = this.getActiveAccount();
    const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;

    const email =
      this.readString(claims, 'email') ??
      this.readFirstArrayString(claims, 'emails') ??
      account?.username ??
      this.readSessionValue('userEmail') ??
      '';

    const fullName = account?.name ?? this.readSessionValue('userName') ?? email;

    return { fullName, email, firstName: null, lastName: null, role: null };
  }

  /**
   * Same contract as getUserContext(), enriched with the idb `accountInfo` (Name/LastName)
   * and `userRoles` catalogs when the idb service is available.
   */
  getUserContext$(): Observable<ChatbotUserContext> {
    const context = this.getUserContext();

    if (!this.idbService) {
      return of(context);
    }

    return forkJoin([
      from(this.idbService.select(IDB_ACCOUNT_INFO)).pipe(catchError(() => of(null))),
      this.selectUserRoles$(),
    ]).pipe(
      map(([accountInfo, roles]: [AccountInfoRecord | null, UserRoleRecord[] | null]) =>
        this.mergeIdbContext(context, accountInfo, roles),
      ),
      catchError(() => of(context)),
    );
  }

  /** Resolves RoleCode through the host lookup, falling back to IndexedDB when needed. */
  getRoleCode$(): Observable<string | null> {
    const roleCode$ = this.roleLookup
      ? this.roleLookup.getRoleCode$()
      : this.selectUserRoles$().pipe(
          map((roles) => (Array.isArray(roles) && roles.length ? roles[0]?.RoleCode || null : null)),
        );

    return roleCode$.pipe(
      take(1),
      catchError((error) => {
        return this.selectUserRoles$().pipe(
          take(1),
          map((roles) => (Array.isArray(roles) && roles.length ? roles[0]?.RoleCode || null : null)),
        );
      }),
    );
  }

  private selectUserRoles$(): Observable<UserRoleRecord[] | null> {
    if (!this.idbService) {
      return of(null);
    }

    return from(this.idbService.select(IDB_USER_ROLES) as Promise<UserRoleRecord[] | null>).pipe(
      catchError((error) => {
        return of(null);
      }),
    );
  }

  private mergeIdbContext(
    context: ChatbotUserContext,
    accountInfo: AccountInfoRecord | null,
    roles: UserRoleRecord[] | null,
  ): ChatbotUserContext {
    const firstName = accountInfo?.Name?.trim() || null;
    const lastName = accountInfo?.LastName?.trim() || null;
    const composedName = [firstName, lastName].filter(Boolean).join(' ');

    return {
      ...context,
      firstName,
      lastName,
      fullName: composedName || context.fullName,
      email: context.email || accountInfo?.UserName || '',
      role: this.resolveRole(roles),
    };
  }

  private resolveRole(records: UserRoleRecord[] | null): string | null {
    if (!Array.isArray(records) || !records.length) {
      return null;
    }

    const first = records[0];

    return first?.Description || first?.RoleCode || null;
  }

  private getActiveAccount() {
    if (!this.msalService) {
      return null;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();

    return accounts.length ? accounts[0] : null;
  }

  private readString(claims: Record<string, unknown>, key: string): string | undefined {
    const value = claims[key];

    return typeof value === 'string' && value ? value : undefined;
  }

  private readFirstArrayString(claims: Record<string, unknown>, key: string): string | undefined {
    const value = claims[key];

    if (!Array.isArray(value) || !value.length) {
      return undefined;
    }

    const first = value[0];

    return typeof first === 'string' && first ? first : undefined;
  }

  private readSessionValue(key: string): string | undefined {
    if (typeof sessionStorage === 'undefined') {
      return undefined;
    }

    return sessionStorage.getItem(key) || undefined;
  }
}
